import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Box, Heading, Grid, ResponsiveContext, Spinner } from 'grommet'; 
import { DashboardCard } from '.';
import { loadIcon } from '../utils/IconUtils';
import { getCSVData, getJSONData, getFileTypeFromPath } from '../utils/DataUtils';

export const DashboardGrid = ({ filePath, ...rest }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const size = useContext(ResponsiveContext);

  const processRow = useCallback(async (row) => {
    const { section, title, description, cta, url, icon, iconColor } = row;
    if (!section || !title || !description) return null;

    const IconComponent = await loadIcon(icon) || await loadIcon('Link');
    
    return {
      section,
      item: { 
        title, 
        description, 
        cta, 
        url, 
        // Using text-strong for the icon ensures it pops on glass
        icon: <IconComponent color={iconColor || 'text-strong'} size="large" />,
      }
    };
  }, []);

  const groupData = useCallback((rawData) => {
    const grouped = rawData.reduce((acc, item) => {
      if (item) {
        if (!acc[item.section]) acc[item.section] = { section: item.section, items: [] };
        acc[item.section].items.push(item.item);
      }
      return acc;
    }, {});
    return Object.values(grouped);
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const fileType = getFileTypeFromPath(filePath);
        const rawData = await (fileType === 'csv' ? getCSVData(filePath) : getJSONData(filePath));
        const processed = await Promise.all(rawData.map(processRow));
        if (mounted) setData(groupData(processed));
      } catch (err) {
        console.error("DashboardGrid Error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, [filePath, processRow, groupData]);

  const getGridColumns = () => (['xsmall', 'small'].includes(size) ? '100%' : 'medium');

  if (loading) return (
    <Box align="center" justify="center" fill pad="xlarge">
      <Spinner size="medium" color="brand" />
    </Box>
  );

  return (
    <Box direction="column" background="transparent" {...rest}>
      {data.map((section, idx) => (
        <Box key={idx} pad={{ bottom: 'large' }} background="transparent">
          {section.section && (
            <Heading 
              level={2} 
              size="small" 
              weight="bold" 
              margin={{ bottom: 'medium' }}
              color="text-strong" 
            >
              {section.section}
            </Heading>
          )}
          <Grid columns={getGridColumns()} gap="medium">
            {section.items.map((item, itemIdx) => (
              <DashboardCard key={itemIdx} card={item} />
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};